import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Button,
    Chip,
    Typography,
    type TypographyProps,
} from '@heroui/react';
import { StepBackButton } from '@/pages/catalogImport/components';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { NameSection, NamesCheckAlert } from './components';
import type { NameOption } from './nameOptions';
import type { UseNamesStepResult } from './useNamesStep';

type NamesStepViewProps = Omit<UseNamesStepResult, 'hasPreview'>;

// The title is the step's only page-level heading.
const asH1: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h1 {...domProps}>{children}</h1>;

const NO_OPTIONS: NameOption[] = [];

/**
 * Review names: the title, the brands, categories and suppliers sections
 * (the categories one ends with the rows that stay Unsorted), why the last
 * check stayed here, and the footer, left to right: Back to File, the
 * notice that nothing is written yet and that leaving loses these
 * decisions, how many names still need a choice (a warning chip), and Check
 * products. On a phone the footer is a column: the notice and the chip on
 * one line, Check products, then Back.
 */
export const NamesStepView: React.FC<NamesStepViewProps> = ({
    sections,
    unsortedRows,
    catalog,
    onRetryCatalog,
    check,
    incomplete,
    canContinue,
    onActionChange,
    onTargetChange,
    onNameChange,
    onParentChange,
    onAddAxesChange,
    onAcceptAll,
    onBack,
    onContinue,
}) => {
    const { t } = useTranslation('pages');
    const isCatalogLoading = catalog.status === 'loading';
    const options = catalog.status === 'ready' ? catalog.options : null;

    return (
        <section
            className="flex flex-col gap-6 py-4 sm:py-8"
            {...getNodeTestIdProp('catalog-import-names')}
        >
            <div className="flex flex-col gap-2">
                <Typography
                    render={asH1}
                    type="h2"
                    {...getNodeTestIdProp('catalog-import-names-title')}
                >
                    {t('catalogImport.names.title')}
                </Typography>
                <Typography.Paragraph color="muted">
                    {t('catalogImport.names.intro')}
                </Typography.Paragraph>
            </div>

            {catalog.status === 'error' ? (
                <Alert
                    status="warning"
                    {...getNodeTestIdProp('catalog-import-names-catalog-error')}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t('catalogImport.names.catalogFailed.title')}
                        </Alert.Title>
                        <Alert.Description>
                            {t('catalogImport.names.catalogFailed.description')}
                        </Alert.Description>
                        <Button
                            className="mt-2 self-start"
                            onPress={onRetryCatalog}
                            size="sm"
                            variant="secondary"
                            {...getNodeTestIdProp(
                                'catalog-import-names-catalog-retry',
                            )}
                        >
                            {t('catalogImport.names.catalogFailed.retry')}
                        </Button>
                    </Alert.Content>
                </Alert>
            ) : null}

            {sections.map((section) => (
                <NameSection
                    existing={options?.[section.kind] ?? NO_OPTIONS}
                    isCatalogLoading={isCatalogLoading}
                    key={section.kind}
                    onAcceptAll={onAcceptAll}
                    onActionChange={onActionChange}
                    onAddAxesChange={onAddAxesChange}
                    onNameChange={onNameChange}
                    onParentChange={onParentChange}
                    onTargetChange={onTargetChange}
                    parents={options?.categories ?? NO_OPTIONS}
                    section={section}
                    unsortedRows={
                        section.kind === 'categories' ? unsortedRows : 0
                    }
                />
            ))}

            {check.status === 'failed' ||
            check.status === 'blocked' ||
            check.status === 'namesNeedAttention' ||
            check.status === 'axesToReview' ? (
                <NamesCheckAlert check={check} />
            ) : null}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <StepBackButton
                    className="order-3 w-full sm:order-1 sm:w-auto"
                    onPress={onBack}
                    target="file"
                />
                {/* The count drops under the notice, still on the right, when
                    the two do not fit on one line (Georgian on a phone). */}
                <div className="order-1 flex flex-wrap items-center gap-3 sm:order-2 sm:flex-1">
                    <Typography
                        className="grow basis-40"
                        color="muted"
                        type="body-xs"
                        {...getNodeTestIdProp(
                            'catalog-import-names-not-written',
                        )}
                    >
                        {t('catalogImport.names.notWritten')}
                    </Typography>
                    {incomplete > 0 ? (
                        <Chip
                            className="ml-auto shrink-0"
                            color="warning"
                            size="sm"
                            variant="soft"
                            {...getNodeTestIdProp(
                                'catalog-import-names-incomplete',
                            )}
                        >
                            {t('catalogImport.names.incomplete', {
                                count: incomplete,
                            })}
                        </Chip>
                    ) : null}
                </div>
                <Button
                    className="order-2 w-full sm:order-3 sm:w-auto"
                    isDisabled={!canContinue}
                    isPending={check.status === 'checking'}
                    onPress={onContinue}
                    variant="primary"
                    {...getNodeTestIdProp('catalog-import-names-continue')}
                >
                    {t('catalogImport.names.continue')}
                </Button>
            </div>
        </section>
    );
};
